import Head from 'next/head';
import { Layout } from 'src/components/layout';
import React, { ReactNode, useState, useEffect, useReducer } from 'react';
import SvgClose from 'src/components/icons/close';
import SvgLogo from 'src/components/icons/logo';
import SvgLogo2 from 'src/components/icons/logo-2';
import SvgArrowBack from 'src/components/icons/arrow-back';
import SvgCompass from 'src/components/icons/compass';
import SvgEmailSent from 'src/components/icons/email-sent-2';
import SvgEmailResend from 'src/components/icons/email-resend';
import SvgEmailVerified from 'src/components/icons/email-verified';
import { useRouter } from 'next/router';
import {
  Image,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Checkbox,
  Link as ChakraLink,
  Button,
  Text,
  Box,
} from '@chakra-ui/react';
import { useIsTablet, useIsMobile } from 'src/hooks/use-is-mobile';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { logger } from 'src/lib/logger';
import { FORM_ERROR_MSG_REQUIRED } from 'src/constants/valid';
import { PasswordInput } from 'src/app/auth/components/password-input';
import Link from 'next/link';
import { userReducer, initState } from 'src/contexts/UserContext';

import {
  useSignUp,
  useCodeVerify,
  useCodeConsume,
  useResendEmail,
  useChangeEmail,
  useActivate,
  setAuthToken,
  setSentryUser,
} from 'src/app/auth/use-auth';
import { useDebounce } from 'use-debounce';
import { RestResponse } from 'src/types';
import { axios } from 'src/lib/axios';
// import { getLocalStorage } from 'src/heplers/localStorage';

export function CodeLayout({ children }: { children: ReactNode }) {
  const router = useRouter();
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();

  function checkIsTabletMobile(
    baseSize: any,
    tabletSize?: any,
    mobileSize?: any
  ) {
    if (isMobile) {
      return mobileSize || tabletSize;
    }
    if (isTablet) {
      return tabletSize;
    }
    return baseSize;
  }
  return (
    <>
      <Head>
        <meta name="code" content="noindex" />
      </Head>
      <Layout>
        <Box
          style={{
            width: '100vw',
            height: '100vh',
            position: 'fixed',
            top: 0,
          }}
          zIndex="999"
        >
          <Image
            src={checkIsTabletMobile(
              require('images/assets/onboarding/onboarding-desktop.jpg'),
              require('images/assets/onboarding/onboarding-tablet.jpg')
            )}
            objectFit="cover"
            width="100%"
            height="100vh"
          />
          {isMobile ? (
            <SvgLogo2
              alt="1TM Logo"
              className="absolute top-6 mx-auto left-0 right-0 text-black text-gray-300 text-2xl h-5 z-50"
            />
          ) : (
            <SvgLogo
              alt="1TM Logo"
              className="absolute top-3 left-6 text-black text-gray-300 text-5xl h-5"
            />
          )}
          <div
            role="button"
            tabIndex={0}
            className={`absolute top-5 right-5 flex p-2 z-50 ${checkIsTabletMobile(
              'text-white',
              'text-white',
              'text-gray-900'
            )}`}
            onClick={() => router.push('/')}
            onKeyDown={(e) => {
              if (e.code === 'Enter') {
                router.push('/');
              }
            }}
          >
            <SvgClose />
          </div>
          {children}
          <div
            style={{
              maxWidth: checkIsTabletMobile('905px', null, '100%'),
              color: '#E4EEFF',
            }}
            className="absolute bottom-5 text-center mx-auto left-0 right-0 z-auto text-xs"
          >
            <div className="py-1">
              Connecting Creativity and People, Making Ideas Happen!
            </div>
            <div className="py-1">Copyright @ 1TM</div>
          </div>
        </Box>
      </Layout>
    </>
  );
}

export function InviteCode() {
  const router = useRouter();
  const { t } = useTranslation();
  const [isVerified, setIsVerified] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  const methods = useForm<{
    code: string;
    username: string;
    email: string;
    remail: string;
  }>({
    mode: 'onBlur',
  });
  const { register, handleSubmit, errors, setError } = methods;
  const [localError, setLocalError] = useState<any>({});
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isResendEmail, setIsResendEmail] = useState(false);
  const [isMain, setIsMain] = useState(false);
  const [isAuth, setIsAuth] = useState(false);
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();
  const { isLoading, mutateAsync: createUser } = useSignUp();
  const { mutateAsync: verifyCode } = useCodeVerify();
  const { mutateAsync: consumeCode } = useCodeConsume();
  const { mutateAsync: resendEmail } = useResendEmail();
  const { mutateAsync: changeEmail } = useChangeEmail();
  const { mutateAsync: activateUser } = useActivate();
  const [listedErrors, setListedErrors] = useState<any>([]);
  // const routeLink = getLocalStorage('routeLink') || '/';
  const localErrors = {
    username: {
      title: 'Username is taken.',
      message: 'Please select another username.',
    },
    email: {
      title: 'Email has been taken.',
      message: 'Please confirm your email address.',
    },
    remail: {
      title: 'Email has been taken.',
      message: 'Please confirm your email address.',
    },
    password: {
      title: 'Password error.',
      message:
        'Passwords must be at least 8 characters, including one upper and lower case letter, and one digit. Blank spaces are not allowed.',
    },
    code: {
      title: 'Invalid code',
      message: 'Please input again',
    },
    codeVerified: {
      title: 'Congratulations!',
      message: 'Invitation code verified!',
    },
  };

  const [showEmailErrorMsg, setShowEmailErrorMsg] = useState(false);
  const [reenterValue, setReenterValue] = useState('');
  const handleChange = (event) => setReenterValue(event.target.value);

  const [boxValue, setBoxValue] = useState(false);

  function checkIsTabletMobile(
    baseSize: any,
    tabletSize?: any,
    mobileSize?: any
  ) {
    if (isMobile) {
      return mobileSize || tabletSize;
    }
    if (isTablet) {
      return tabletSize || mobileSize;
    }
    return baseSize;
  }

  const [keycode, setCode] = useState();
  const handleCode = (event) => setCode(event.target.value);
  const [debouncedKeyword] = useDebounce(keycode, 150);
  const [isCodeVerified, setIsCodeVerified] = useState(true);
  const [registereduser, setRegisteredUser] = useState<any>();
  const [activationToken, setActivationToken] = useState<any>();
  const [state, userDispatch] = useReducer(userReducer, initState);
  const [codeConfirm, setCodeConfirm] = useState(false);
  const [currUsername, setCurrUsername] = useState<string>();

  useEffect(() => {
    async function verificationCode() {
      if (debouncedKeyword) {
        try {
          const codeToVerify = { invitationCode: debouncedKeyword };
          const checkUserCode = await verifyCode(codeToVerify);
          if (checkUserCode?.code === 1000) {
            setIsCodeVerified(true);
            setListedErrors(['codeVerified']);
          } else {
            setListedErrors(['code']);
            setIsCodeVerified(false);
            setCodeVerified(false);
          }
        } catch (err) {
          logger.error(err);
        }
      }
    }
    verificationCode();
  }, [debouncedKeyword]);

  useEffect(() => {
    async function consumingCode() {
      if (isEmailSent) {
        try {
          const codeToConsume = {
            invitationCode: debouncedKeyword,
            username: registereduser?.username,
          };
          await consumeCode(codeToConsume);
        } catch (err) {
          logger.error(err);
        }
      }
    }
    consumingCode();
  }, [isEmailSent, setIsEmailSent]);

  const emailResend = async (data) => {
    const emailToResend = {
      email: data,
    };
    await resendEmail(emailToResend);
  };

  useEffect(() => {
    async function activatingUser() {
      if (activationToken) {
        try {
          const toActivation = { token: activationToken };
          const activateResponse = await activateUser(toActivation);
          if (activateResponse.code === 1000) {
            const { data: resData } = activateResponse;
            setCurrUsername(resData.data.username);
            setAuthToken(resData.token);
            setSentryUser({
              id: resData.data.id,
              username: resData.data.username,
              firstName: resData.data.firstName,
              lastName: resData.data.lastName,
              email: resData.data.email,
              userRole: resData.data.userRole,
            });
            userDispatch({ type: 'FETCH_USER', data: resData.data });
            console.log(state);
            setCodeConfirm(true);
          }
        } catch (err) {
          logger.error(err);
        }
      }
    }
    activatingUser();
  }, [activationToken, setActivationToken]);

  const clearToast = () => {
    setListedErrors([]);
  };

  const onSubmit = async (data) => {
    try {
      const createUserResponse = await createUser(data);
      if (createUserResponse?.code === 1000) {
        setRegisteredUser(data);
        setCodeVerified(false);
        setIsResendEmail(false);
        setIsMain(false);
        setIsAuth(false);
        setIsEmailSent(true);
        setStartTick(true);
      }

      if (createUserResponse?.code === 1011) {
        setError('username', {
          message: 'Please select another username.',
        });
        setLocalError({
          username: {
            message: 'Please select another username.',
          },
        });
      }

      if (createUserResponse?.code === 1004) {
        setError('email', {
          message: 'Please confirm your email address.',
        });
        setLocalError({
          email: {
            message: 'Please confirm your email address.',
          },
        });
      }
    } catch (err) {
      logger.error(err);
    }
  };
  const [seconds, setSeconds] = useState(30);
  const [over, setOver] = useState(false);
  const [startTick, setStartTick] = useState(false);

  const tick = () => {
    if (over) return;
    if (seconds === 1) {
      setOver(true);
      setStartTick(false);
    } else {
      setSeconds(seconds - 1);
    }
  };

  const resendNewEmail = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reenterValue)) {
      setShowEmailErrorMsg(true);
    } else {
      setShowEmailErrorMsg(false);

      const changeEmailToResend = {
        newEmail: reenterValue,
        email: registereduser.email,
        password: registereduser.password,
      };

      const changeWait = await changeEmail(changeEmailToResend);

      if (changeWait.code === 1000) {
        const newUserEmail = { ...registereduser };
        newUserEmail.email = reenterValue;
        setRegisteredUser(newUserEmail);
        emailResend(reenterValue);
        setIsEmailSent(true);
        setIsResendEmail(false);
        setStartTick(true);
      }
      if (changeWait.code === 1004) {
        setListedErrors(['remail']);
        setReenterValue('');
      }
    }
  };

  useEffect(() => {
    if (startTick) {
      const timerID = setInterval(() => tick(), 1000);
      return () => clearInterval(timerID);
    }
  }, [startTick, tick]);

  const goToStudio = async () => {
    await axios.post<RestResponse<any>>(`/user/landed`);
    router.push(`${currUsername}`);
  };

  useEffect(() => {
    setListedErrors(Object.keys(errors));
  }, [errors, setError, localError, setLocalError]);

  useEffect(() => {
    if (router.query['token']) {
      setCodeVerified(false);
      setIsEmailSent(false);
      setIsResendEmail(false);
      setIsMain(false);
      setIsAuth(true);
      setActivationToken(router.asPath.split('=')[1]);
    } else if (router.query['noCode']) {
      setCodeVerified(true);
      setIsEmailSent(false);
      setIsResendEmail(false);
      setIsMain(false);
      setIsAuth(false);
    } else {
      setIsMain(true);
    }
  }, [router]);
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div
        style={{
          backgroundColor: '#F4F7F6',
          maxWidth: checkIsTabletMobile('905px', null, '100%'),
          height: checkIsTabletMobile('590px', '80%', '100%'),
        }}
        className={`z-10 absolute top-0 bottom-0 right-0 left-0 text-gray-900 w-auto ${checkIsTabletMobile(
          'mx-auto rounded-xl my-auto',
          'mx-10 my-auto p-10 rounded-xl',
          'm-auto'
        )}`}
      >
        <div className="h-full w-full">
          {codeVerified && !router.query['noCode'] && (
            <div
              role="button"
              className="absolute top-8 left-8"
              tabIndex={0}
              onClick={() => {
                setCodeVerified(false);
                clearToast();
              }}
              onKeyDown={(e) => {
                if (e.code === 'Enter') {
                  setCodeVerified(false);
                  clearToast();
                }
              }}
            >
              <SvgArrowBack />
            </div>
          )}
          <div
            style={{ height: 'fit-content' }}
            className={`${checkIsTabletMobile(
              codeVerified ? 'px-20 h-2/3' : 'px-36',
              codeVerified ? 'px-20' : 'px-8',
              codeVerified ? 'px-12 h-4/5' : 'px-12'
            )} absolute m-auto top-0 bottom-0 left-0 right-0`}
          >
            {!codeVerified && isMain && (
              <div className="pb-16">
                <div style={{ width: 'fit-content' }} className="block m-auto">
                  <div
                    style={{
                      fontSize: checkIsTabletMobile('40px', '20px', '20px'),
                    }}
                    className={`inline-block my-auto mx-2 ${checkIsTabletMobile(
                      'mt-5',
                      'mt-12',
                      'mt-32'
                    )}`}
                  >
                    Welcome to
                  </div>
                  <SvgLogo
                    alt="1TM Logo"
                    className="inline-block text-gray-900 text-gray-300 align-baseline"
                    height="auto !important"
                    style={{
                      fontSize: checkIsTabletMobile('115px', '60px', '60px'),
                    }}
                  />
                </div>
                <div
                  style={{ width: 'fit-content' }}
                  className="text-gray-700 m-auto text-center"
                >
                  A Collaborative Platform for Creators, Enablers and
                  Enthusiasts
                </div>
                <div
                  style={{ width: 'fit-content' }}
                  className="m-auto my-3 flex text-gray-300 text-3xl"
                >
                  <span className="mx-3">.</span>
                  <span className="mx-3">.</span>
                  <span className="mx-3">.</span>
                </div>
                <div
                  style={{ width: 'fit-content', fontSize: '20px' }}
                  className="m-auto font-medium my-5"
                >
                  I have an invitation code!
                </div>
                <div
                  style={{ width: 'fit-content', fontSize: '14px' }}
                  className="m-auto my-5 text-center text-gray-700 "
                >
                  If you have an invitation code, please enter the code for
                  verification.
                </div>
                <div className="m-auto my-5">
                  <FormControl id="code" isInvalid={!!errors?.code}>
                    <Input
                      id="code-input"
                      data-testid="code"
                      name="code"
                      placeholder={t('Enter your invitation code')}
                      onChange={handleCode}
                      value={keycode}
                      focusBorderColor="black"
                      ref={register({
                        required: true,
                      })}
                      style={{ width: '100%', height: '40px' }}
                    />
                  </FormControl>
                </div>
                <div style={{ width: 'fit-content' }} className="m-auto">
                  <button
                    type="button"
                    tabIndex={0}
                    style={{ width: '110px', height: '40px' }}
                    className={`rounded-full text-white my-5 ${
                      errors?.code || !isCodeVerified
                        ? 'bg-blue-200'
                        : 'bg-blue-500'
                    }`}
                    onClick={() => {
                      if (isCodeVerified) {
                        setCodeVerified(true);
                        clearToast();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.code === 'Enter') {
                        if (isCodeVerified) {
                          setCodeVerified(true);
                          clearToast();
                        }
                      }
                    }}
                  >
                    Verify
                  </button>
                </div>
                <div
                  style={{ width: 'fit-content' }}
                  className="m-auto flex my-5 text-gray-500 text-center"
                >
                  <div>
                    Need an invitation code? Sign up at the
                    <button
                      type="button"
                      className="ml-1 text-blue-500"
                      tabIndex={0}
                      onClick={() => {
                        router.push('https://www.1tm.io/');
                      }}
                      onKeyDown={(e) => {
                        if (e.code === 'Enter') {
                          router.push('https://www.1tm.io/');
                        }
                      }}
                    >
                      Waitlist
                    </button>
                    !
                  </div>
                </div>
              </div>
            )}
            {isAuth && (
              <div className="pb-16">
                <div
                  className={`text-gray-900 m-auto text-center ${checkIsTabletMobile(
                    '',
                    'px-18',
                    'px-8'
                  )}`}
                >
                  <div className="w-full py-2 mt-5">
                    <SvgEmailVerified
                      width="80px"
                      height="80px"
                      className="mx-auto"
                    />
                  </div>
                  <div className="text-4xl py-2">Email Verified!</div>
                  <div className="">
                    Your sign up process is now complete, please proceed to the
                    onboarding process!
                  </div>
                  <Stack className="pt-4" spacing={3} alignItems={['center']}>
                    <Button
                      rounded="ellipse"
                      size="md"
                      disabled={!codeConfirm}
                      width="auto"
                      type="submit"
                      onClick={() => goToStudio()}
                    >
                      {t('Continue')}
                    </Button>
                  </Stack>
                </div>
              </div>
            )}
            {isEmailSent && (
              <div className="pb-16">
                <div
                  className={`text-gray-900 m-auto text-center ${checkIsTabletMobile(
                    '',
                    'px-18',
                    'px-8'
                  )}`}
                >
                  <div className="w-full py-2 mt-5">
                    <SvgEmailSent
                      width="80px"
                      height="80px"
                      className="mx-auto"
                    />
                  </div>
                  <div className="text-4xl py-2">Verification Email Sent!</div>
                  <div className="">
                    We have sent a verification email to
                    <span className="text-blue-500 px-1">
                      {registereduser?.email}
                    </span>
                    please check your email and click &quot;verify&quot; to
                    complete the account sign up process!
                  </div>
                  <Stack className="pt-4" spacing={3} alignItems={['center']}>
                    <Button
                      rounded="ellipse"
                      size="md"
                      width="auto"
                      type="button"
                      isDisabled={!over}
                      onClick={() => {
                        setOver(false);
                        setSeconds(30);
                        setStartTick(true);
                        emailResend(registereduser?.email);
                      }}
                    >
                      {over ? (
                        t('Resend Email')
                      ) : (
                        <span>
                          {t('Resend Email')} {seconds} {t('sec')}
                        </span>
                      )}
                    </Button>

                    <Text
                      role="button"
                      tabIndex={0}
                      className="text-blue-500"
                      fontSize="sm"
                      onClick={() => {
                        setIsResendEmail(true);
                        setIsEmailSent(false);
                        setReenterValue('');
                      }}
                      onKeyDown={(e) => {
                        if (e.code === 'Enter') {
                          setIsResendEmail(true);
                          setIsEmailSent(false);
                          setReenterValue('');
                        }
                      }}
                    >
                      <span>{t('Edit Email')}</span>
                    </Text>
                  </Stack>
                </div>
              </div>
            )}
            {isResendEmail && (
              <div className="pb-16">
                <div
                  className={`text-gray-900 m-auto text-center ${checkIsTabletMobile(
                    '',
                    'px-18',
                    'px-8'
                  )}`}
                >
                  <div className="w-full py-2 mt-5">
                    <SvgEmailResend
                      width="80px"
                      height="80px"
                      className="mx-auto"
                    />
                  </div>
                  <div className="text-4xl py-2">Re-enter Email</div>
                  <div className="">
                    Please re-enter your email below to continue.
                  </div>
                  <Stack className="pt-4" spacing={3} alignItems={['center']}>
                    <Input
                      id="remail-input"
                      data-testid="remail"
                      name="remail"
                      type="email"
                      style={{
                        fontFeatureSettings: `'calt' 0`,
                        border: showEmailErrorMsg
                          ? '1px solid #FF6633'
                          : '1px solid #D3D6D8',
                      }}
                      className={`${
                        showEmailErrorMsg
                          ? 'bg-outrageousOrange-500 bg-opacity-10'
                          : 'bg-white'
                      }`}
                      value={reenterValue}
                      onChange={handleChange}
                      autoComplete="email"
                      placeholder={t('welcome@1tm.io')}
                      variant="outlined"
                      focusBorderColor="black"
                    />
                    {showEmailErrorMsg && (
                      <p
                        className="text-outrageousOrange-500 text-xs font-medium text-left w-full"
                        style={{
                          lineHeight: '18px',
                          marginTop: '0.25rem',
                          marginLeft: '0.75rem',
                        }}
                      >
                        {t('Please confirm your email address.')}
                      </p>
                    )}
                    <Button
                      rounded="ellipse"
                      size="md"
                      width="auto"
                      type="button"
                      isDisabled={!reenterValue && !errors?.remail}
                      onClick={resendNewEmail}
                    >
                      {t('Confirm')}
                    </Button>
                  </Stack>
                </div>
              </div>
            )}
            {codeVerified && (
              <div className="h-auto">
                <div
                  className={`text-gray-900 m-auto ${checkIsTabletMobile(
                    'grid grid-cols-2 gap-14 ',
                    'grid-rows-2'
                  )}`}
                >
                  <div className="text-center">
                    <div className="w-full py-2 mt-5">
                      <SvgCompass
                        width="80px"
                        height="80px"
                        className="mx-auto"
                      />
                    </div>
                    <div className="text-4xl py-2">{t('Get Started')}</div>
                    <div className=" py-2">
                      {t(
                        'A Collaborative Platform for Creators, Enablers and Enthusiasts'
                      )}
                    </div>
                  </div>
                  <div className="">
                    <Stack spacing={[3, 2, 4]}>
                      <FormControl id="email" isInvalid={!!errors?.email}>
                        <FormLabel mb={0} fontSize="sm">
                          {t('Email')}
                        </FormLabel>
                        <Input
                          id="email-input"
                          data-testid="email"
                          name="email"
                          type="email"
                          style={{
                            fontFeatureSettings: `'calt' 0`,
                          }}
                          autoComplete="email"
                          placeholder={t('welcome@1tm.io')}
                          variant="outlined"
                          focusBorderColor="black"
                          ref={register({
                            required: true,
                            pattern: {
                              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                              message: t('Invalid Email Address'),
                            },
                          })}
                        />
                      </FormControl>

                      <FormControl id="username" isInvalid={!!errors.username}>
                        <FormLabel mb={0} fontSize="sm">
                          {t('Username')}
                        </FormLabel>
                        <Input
                          data-testid="username"
                          autoComplete="username"
                          name="username"
                          type="text"
                          ref={register({
                            required: {
                              value: true,
                              message: FORM_ERROR_MSG_REQUIRED,
                            },
                            pattern: {
                              // eslint-disable-next-line no-useless-escape
                              value: /^[-\w\.\$]{4,20}$/,
                              message: t('4 to 20 characters only'),
                            },
                          })}
                          placeholder={t('Create a unique username')}
                          variant="outlined"
                          focusBorderColor="black"
                        />
                      </FormControl>

                      <PasswordInput id="password" methods={methods} isCode />

                      <Stack
                        direction="row"
                        justifyContent={[
                          'center',
                          'center',
                          'center',
                          'flex-start',
                        ]}
                      >
                        <Checkbox
                          name="agree"
                          onChange={(e) => setBoxValue(e.target.checked)}
                        />{' '}
                        <Text fontSize="xs">
                          {t('I agree to')}{' '}
                          <Link href="/#terms" passHref>
                            <ChakraLink>{t('Terms and Conditions')}</ChakraLink>
                          </Link>
                        </Text>
                      </Stack>

                      <Stack
                        spacing={3}
                        alignItems={[
                          'center',
                          'center',
                          'center',
                          'flex-start',
                        ]}
                      >
                        <Button
                          rounded="ellipse"
                          size="md"
                          width="130px"
                          type="submit"
                          isDisabled={isLoading || !boxValue}
                        >
                          {t('Register')}
                        </Button>

                        <Text color="$mode.500" fontSize="sm">
                          {t('Already have an account?')}{' '}
                          <Link href="/agora?page=signIn" passHref>
                            <ChakraLink>{t('Sign in')}</ChakraLink>
                          </Link>
                        </Text>
                      </Stack>
                    </Stack>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {isVerified && (
        <div
          className={`absolute text-gray-900 bg-white h-24 ${checkIsTabletMobile(
            'rounded w-64 top-16 right-5',
            'w-full top-0 z-50'
          )}`}
        >
          <div className="relative h-full w-full p-5">
            <div
              role="button"
              className={`absolute ${checkIsTabletMobile(
                'top-2 right-2 text-gray-400',
                'top-5 right-5 text-gray-700'
              )}`}
              tabIndex={0}
              onClick={() => setIsVerified(false)}
              onKeyDown={(e) => {
                if (e.code === 'Enter') {
                  setIsVerified(false);
                }
              }}
            >
              <SvgClose className="text-xs" />
            </div>
            <div style={{ color: '#2BC3A8' }} className="py-0.5">
              {t('Congratulations!')}
            </div>
            <div className="py-0.5">{t('Invitation code verified!')}</div>
          </div>
        </div>
      )}

      <div
        className={`absolute text-gray-900 z-50 ${checkIsTabletMobile(
          'top-16 right-5',
          'top-0 w-full'
        )}`}
      >
        {listedErrors.map((err) => (
          <div key={err} className={checkIsTabletMobile('py-2', '')}>
            <div
              className={`relative p-5 bg-white ${checkIsTabletMobile(
                'rounded w-64',
                'w-full'
              )}`}
            >
              <div
                role="button"
                className={`absolute ${checkIsTabletMobile(
                  'top-2 right-2 text-gray-400',
                  'top-5 right-5 text-gray-700'
                )}`}
                tabIndex={0}
                onClick={() => {
                  setListedErrors(listedErrors.filter((item) => item !== err));
                }}
                onKeyDown={(e) => {
                  if (e.code === 'Enter') {
                    setListedErrors(
                      listedErrors.filter((item) => item !== err)
                    );
                  }
                }}
              >
                <SvgClose className="text-xs" />
              </div>
              {err === 'codeVerified' ? (
                <div style={{ color: '#2BC3A8' }} className="py-0.5">
                  {t(localErrors[err]?.title)}
                </div>
              ) : (
                <div style={{ color: '#FF6633' }} className="py-0.5">
                  {t(localErrors[err]?.title)}
                </div>
              )}
              <div className="py-0.5">{t(localErrors[err]?.message)}</div>
            </div>
          </div>
        ))}
      </div>
    </form>
  );
}
